import React from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";

const SUPPORT_EMAIL = "ziv@stayinnovation.com";

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  maxWidth: "900px",
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.2)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
}));

const ContactCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2.5),
  borderRadius: "12px",
  border: "1px solid rgba(0,202,153,0.35)",
  background: "rgba(0,202,153,0.08)",
  marginBottom: theme.spacing(4),
}));

const faqs = [
  {
    q: "How do I get help or report a problem?",
    a: `Email us at ${SUPPORT_EMAIL} with a short description of the issue and, if possible, a screenshot. We read every message and reply directly.`,
  },
  {
    q: "How do I manage or cancel my subscription?",
    a: "Datarate subscriptions (Pro and Pro Plus) are billed through your Apple ID. Open the iOS Settings app, tap your name, tap Subscriptions, select Datarate, and you can upgrade, downgrade, or cancel at any time. Cancellation stops future renewals; you keep access until the end of the current period.",
  },
  {
    q: "How do I request a refund?",
    a: "Purchases are processed by Apple. Refund requests are handled through Apple at reportaproblem.apple.com. If you believe you were charged in error, email us and we'll do our best to help.",
  },
  {
    q: "How do I delete my account or my data?",
    a: `Go to Settings inside the app to delete your account, or email ${SUPPORT_EMAIL} from your registered address and we will permanently remove your account and associated data.`,
  },
  {
    q: "I forgot my password or can't sign in.",
    a: `Use the "Forgot password" option on the login screen. If you signed in with Apple or Google and are locked out, email ${SUPPORT_EMAIL} and we'll help you regain access.`,
  },
  {
    q: "What is Datarate?",
    a: "Datarate helps you get AI answers you can trust and organize your reviews, collections, and knowledge. If you have questions about a feature, reach out any time.",
  },
];

const SupportPage = () => {
  const navigate = useNavigate();

  return (
    <StyledContainer>
      <Box sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
      </Box>
      <StyledPaper>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 600, mb: 1 }}
        >
          Datarate Support
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Need help, have a question, or want to report an issue? We're here for
          you.
        </Typography>

        <ContactCard>
          <EmailIcon sx={{ color: "rgb(0,202,153)", fontSize: 36 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Email our support team
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We typically reply within 1–2 business days.
            </Typography>
          </Box>
          <Button
            variant="contained"
            href={`mailto:${SUPPORT_EMAIL}?subject=Datarate%20Support`}
            sx={{
              background: "rgb(0,202,153)",
              "&:hover": { background: "rgb(0,170,128)" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {SUPPORT_EMAIL}
          </Button>
        </ContactCard>

        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          What to include
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          To help us resolve your request faster, please include:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <li>
            <Typography variant="body2" color="text.secondary" component="span">
              Your Datarate account email
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary" component="span">
              The app version and device model
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary" component="span">
              Screenshots or steps to reproduce the issue
            </Typography>
          </li>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <HelpOutlineIcon sx={{ color: "rgb(0,202,153)" }} />
          <Typography
            variant="h6"
            component="h2"
            sx={{ fontWeight: 600 }}
          >
            Frequently asked questions
          </Typography>
        </Box>

        {faqs.map((item, idx) => (
          <Box key={idx} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 0.5 }}
            >
              {item.q}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.a}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          Contact
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Support email:</strong>{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "rgb(0,202,153)" }}>
            {SUPPORT_EMAIL}
          </a>
        </Typography>
        <Typography variant="body2" paragraph>
          You can also review our{" "}
          <span
            onClick={() => navigate("/terms")}
            style={{ color: "rgb(0,202,153)", cursor: "pointer" }}
          >
            Terms &amp; Privacy Policy
          </span>
          .
        </Typography>
      </StyledPaper>
    </StyledContainer>
  );
};

export default SupportPage;
